/**
 * The pure half of «Enviar por correo» (contract v10.26): reading the addresses a person
 * types and saying where a sending stands. Out of the component so the tests pin the
 * words without rendering.
 */
import type { QuoteSending } from './types';

/**
 * The addresses in what was typed or pasted: separated by commas, semicolons, spaces or
 * lines, «Nombre <dirección>» reduced to the address, each once whatever its case. The
 * back drops repeats too; doing it here keeps the field honest about what will be sent.
 */
export function addressesFrom(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const segment of text.split(/[,;\n]+/)) {
    // «Ana Pérez <ana@x.es>» is one address, whatever the name; without brackets, spaces
    // separate addresses too («a@x.es b@y.es»), and a stray word stays, to be flagged.
    const bracketed = /<([^>]*)>/.exec(segment);
    const pieces = bracketed ? [bracketed[1] ?? ''] : segment.split(/\s+/);
    for (const piece of pieces) {
      const address = piece.trim();
      if (!address) continue;
      const key = address.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(address);
    }
  }
  return out;
}

/** The first thing that does not look like an address, or null when all do. */
export function notAnAddress(addresses: string[]): string | null {
  return addresses.find((address) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) ?? null;
}

/** «ana@x.es», «ana@x.es y luis@y.es», «ana@x.es, luis@y.es y 2 más». */
export function recipientsText(addresses: string[]): string {
  if (addresses.length <= 1) return addresses[0] ?? '';
  if (addresses.length === 2) return `${addresses[0]} y ${addresses[1]}`;
  return `${addresses.slice(0, 2).join(', ')} y ${addresses.length - 2} más`;
}

/** After this long without an answer about the copy in Sent, stop waiting for it. */
export const APPEND_WAIT_MS = 2 * 60_000;

export type SendingState =
  | { kind: 'sending' }
  | { kind: 'failed'; reason: string }
  | { kind: 'sent'; copy: 'saved' | 'saving' | 'failed' | 'unknown'; copyError: string | null };

/**
 * Where one sending stands: on its way, refused, or sent, and then what happened to
 * the copy in the mailbox's Sent folder, which is written after the send and can fail
 * on its own without the customer missing anything.
 */
export function sendingState(sending: QuoteSending, now = Date.now()): SendingState {
  if (sending.status === 'pending') return { kind: 'sending' };
  if (sending.status === 'failed') {
    return { kind: 'failed', reason: sending.error ?? 'el servidor de correo no lo aceptó' };
  }
  if (sending.appended_at) return { kind: 'sent', copy: 'saved', copyError: null };
  if (sending.append_error) {
    return { kind: 'sent', copy: 'failed', copyError: sending.append_error };
  }
  const since = Date.parse(sending.sent_at ?? sending.created_at);
  return {
    kind: 'sent',
    copy: Number.isFinite(since) && now - since < APPEND_WAIT_MS ? 'saving' : 'unknown',
    copyError: null,
  };
}

/** Whether the screen has something to wait for: a sending on its way, or its copy. */
export function waitingOnSending(
  sendings: QuoteSending[] | null | undefined,
  now = Date.now(),
): boolean {
  return (sendings ?? []).some((sending) => {
    const state = sendingState(sending, now);
    return state.kind === 'sending' || (state.kind === 'sent' && state.copy === 'saving');
  });
}
